import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, Card, TemporaryPasswordPanel } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useProvisionMerchantMutation } from '../api';
import { ProvisionOrganisationForm } from '../components/ProvisionOrganisationForm';
import { type ProvisionMerchantFormValues } from '../schemas';

import { useToast } from '@/components/ui/toast/ToastProvider';
import { HandOverSheet } from '@/features/handover/components/HandOverSheet';
import { useAssignableAdminsQuery } from '@/features/portfolios/api';
import { useSession } from '@/lib/session';

/** What the screen keeps after a creation: the password lives here, in component state, until the operator says they noted it. */
interface Issued {
  readonly account: string;
  readonly password: string;
  readonly expiresAt: string;
  readonly status: 'active' | 'pending';
}

/**
 * One real endpoint, `POST /admin/organisations` - an operator creating a merchant's account and organisation (decision D1).
 *
 * The answer carries the provisional password, shown ONCE by `TemporaryPasswordPanel` (masked until asked, 60 seconds, then gone). It
 * is never put in a URL, a toast, the console or storage; and it is dropped from this component's state the moment the operator
 * confirms they noted it, or the window ends. A lost password is replaced, not re-displayed.
 *
 * A refusal is told plainly (an address already in use, the daily ceiling, a wrong confirmation password): the caller is an
 * authenticated, audited operator, so there is no enumeration to protect here, and a silent success would hand them a password that
 * opens nothing.
 */
export function ProvisionOrganisationPage(): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const provisionMutation = useProvisionMerchantMutation();
  const { roles } = useSession();
  // A superadmin is not a manager: it picks the administrator who will manage and approve the new merchant. An administrator's
  // merchant joins its own portfolio, decided server side.
  const isSuperadmin = roles.includes('superadmin');
  const managersQuery = useAssignableAdminsQuery(isSuperadmin);
  const [issued, setIssued] = useState<Issued | null>(null);

  async function onSubmit(values: ProvisionMerchantFormValues): Promise<void> {
    try {
      const { managedBy, ...rest } = values;
      const created = await provisionMutation.mutateAsync({
        ...rest,
        ...(managedBy === undefined || managedBy === '' ? {} : { managedBy }),
      });
      toast.success(t('admin.organisations.success'));
      setIssued({
        account: values.email,
        password: created.temporaryPassword,
        expiresAt: created.expiresAt,
        status: created.status,
      });
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
      // Re-thrown so the form keeps what was typed - see its own header.
      throw error;
    }
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">{t('admin.organisations.title')}</h1>
        <p className="text-[var(--color-text-muted)]">{t('admin.organisations.subtitle')}</p>
      </header>

      {issued === null ? (
        <Card>
          <ProvisionOrganisationForm
            onSubmit={onSubmit}
            isSubmitting={provisionMutation.isPending}
            managers={isSuperadmin ? (managersQuery.data ?? []) : undefined}
          />
        </Card>
      ) : (
        <Card>
          <div className="flex flex-col gap-4">
            <p className="text-sm font-medium text-ink">
              {issued.status === 'active'
                ? t('admin.organisations.statusActive')
                : t('admin.organisations.statusPending')}
            </p>
            <TemporaryPasswordPanel
              account={issued.account}
              password={issued.password}
              expiresAt={issued.expiresAt}
              onDone={() => {
                setIssued(null);
              }}
              onExpire={() => {
                setIssued({ ...issued, password: '' });
              }}
            />
            <HandOverSheet email={issued.account} expiresAt={issued.expiresAt} />
            {issued.password === '' ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setIssued(null);
                }}
              >
                {t('admin.organisations.createAnother')}
              </Button>
            ) : null}
          </div>
        </Card>
      )}
    </div>
  );
}
